import type { TimelineItemWithSegment, TimelineSegment } from './timeline-interfaces';
import type { ZoomableTimelineOps } from '@mimir/client-common/timeline/zoomableTimelineOps';

export default class TimelineItem implements TimelineItemWithSegment {

  public static readonly MIN_ITEM_WIDTH = 2;
  public static readonly MIN_DURATION = 500;

  public isResizingLeft: boolean | null = null;


  constructor(
    private item: TimelineSegment,
    private setTime: (ms: number) => void,
    private zoomableTimeline: ZoomableTimelineOps
  ) {}

  onDrag(clampDurationInMs: number, x: number) {
    const startMs = this.zoomableTimeline.pixelToTime(x);
    this.setTimespan(clampDurationInMs, startMs, startMs + this.duration);
  }


  onResizeLeft(clampDurationInMs: number, x: number) {
    const startMs = this.zoomableTimeline.pixelToTime(x);
    this.setTimespan(clampDurationInMs, startMs, this.item.endMs);
  }

  onResizeRight(clampDurationInMs: number, w: number) {
    const newDur = this.zoomableTimeline.pixelToTime(w);
    const startMs = this.item.startMs;
    this.setTimespan(clampDurationInMs, startMs, startMs + newDur);
  }

  setTimespan(clampDurationInMs: number, startMs: number, endMs: number) {
    this.setLoggingTrackTimespan(clampDurationInMs, startMs, endMs);
    const shouldSyncWithStartTime = (this.isResizingLeft !== null && this.isResizingLeft) || this.isResizingLeft === null;
    this.setTime(shouldSyncWithStartTime ? this.item.startMs : this.item.endMs);
  }

  setLoggingTrackTimespan(clampDurationInMs: number, startMs: number, endMs: number) {
    this.item.startMs = this.sanitizeStartTime(clampDurationInMs, startMs);
    const dur = Math.max(endMs - startMs, TimelineItem.MIN_DURATION);
    this.item.endMs = this.sanitizeEndTime(clampDurationInMs, startMs + dur);
  }

  get timelineSegment() {
    return this.item;
  }

  get duration() {
    return (this.item.endMs - this.item.startMs) || TimelineItem.MIN_DURATION;
  }

  getLeft() {
    return this.zoomableTimeline.timeToPixel(this.item.startMs);
  }

  getWidth() {
    const result = this.zoomableTimeline.timeToPixel(this.duration);
    return Math.max(result, TimelineItem.MIN_ITEM_WIDTH);
  }

  getMinWidth() {
    return Math.max(this.zoomableTimeline.timeToPixel(TimelineItem.MIN_DURATION), TimelineItem.MIN_ITEM_WIDTH);
  }

  getMaxWidth(clampDurationInMs: number) {
    return Math.max(this.zoomableTimeline.timeToPixel(clampDurationInMs - this.item.startMs), TimelineItem.MIN_ITEM_WIDTH);
  }

  sanitizeStartTime(clampDurationInMs: number, ms: number) {
    let sanitized = Math.max(ms, 0);
    sanitized = Math.min(sanitized, clampDurationInMs - TimelineItem.MIN_DURATION);
    return sanitized;
  }

  private sanitizeEndTime(clampDurationInMs: number, ms: number) {
    let sanitized = Math.max(ms, TimelineItem.MIN_DURATION);
    sanitized = Math.min(sanitized, clampDurationInMs);
    return sanitized;
  }

}
